import {
  FloatingFocusManager,
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import React, { useState } from "react";
import styled from "styled-components";

import { Fonts } from "assets/fonts";

const TriggerWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border: none;
  text-align: left;
  padding: 0;
`;

const PopupContent = styled.div`
  z-index: 100;
  min-width: 260px;
  border-radius: 4px;
  padding: 12px 16px;
  ${Fonts.regular_14}
  color: ${(p) => p.theme.textHighestContrast};
  background: ${(p) => p.theme.bgHighestContrast};
`;

export interface PopoverProps {
  className?: string;
  onClose?: () => void;
  placement?: Placement;
  renderContent: (close: () => void, isOpen: boolean) => React.ReactNode;
  renderTrigger: () => React.ReactNode;
}
export const Popover = (props: PopoverProps) => {
  const { className, placement, onClose, renderContent, renderTrigger } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement,
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip({ fallbackAxisSideDirection: "end" }), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const focus = useFocus(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role, focus]);

  const close = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={className}>
      <TriggerWrapper ref={refs.setReference} {...getReferenceProps()} className="popover-trigger">
        {renderTrigger()}
      </TriggerWrapper>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <PopupContent
            className="popover-content"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {renderContent(close, isOpen)}
          </PopupContent>
        </FloatingFocusManager>
      )}
    </div>
  );
};
